<script setup lang="ts">
import { computed, ref } from "vue"

import { ICON } from "@/lib/icons"
import type { ImageSummary } from "@/types/models"

import PhotoLightbox from "./PhotoLightbox.vue"

const props = defineProps<{
  images: ImageSummary[]
  alt: string
}>()

const selectedIndex = ref(0)
const lightboxOpen = ref(false)
const lightboxIndex = ref(0)
const mainImage = computed(() => props.images[selectedIndex.value])

function openLightbox(index: number) {
  selectedIndex.value = index
  lightboxIndex.value = index
  lightboxOpen.value = true
}
</script>

<template>
  <button
    v-if="mainImage"
    type="button"
    :aria-label="`Open ${alt} photo ${selectedIndex + 1} of ${images.length}`"
    class="bg-elevated aspect-square cursor-zoom-in overflow-hidden rounded-lg focus-visible:outline-2"
    @click="openLightbox(selectedIndex)"
  >
    <img
      :src="mainImage.large_url ?? mainImage.thumb_url ?? undefined"
      :alt="alt"
      class="size-full object-contain"
    />
  </button>
  <div v-else class="bg-elevated flex aspect-square items-center justify-center rounded-lg">
    <UIcon :name="ICON.item" class="text-dimmed size-12" />
  </div>

  <div v-if="images.length > 1" class="flex gap-2 overflow-x-auto">
    <button
      v-for="(img, i) in images"
      :key="img.id"
      type="button"
      :aria-label="`Open photo ${i + 1} of ${images.length}`"
      class="size-20 shrink-0 cursor-zoom-in overflow-hidden rounded-lg border-2 focus-visible:outline-2"
      :class="i === selectedIndex ? 'border-primary' : 'border-transparent'"
      @click="openLightbox(i)"
    >
      <img :src="img.thumb_url ?? undefined" alt="" class="size-full object-cover" />
    </button>
  </div>

  <PhotoLightbox
    v-model:open="lightboxOpen"
    :images="images"
    :initial-index="lightboxIndex"
    :alt="alt"
  />
</template>
